import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { CloseOutlined, PaperClipOutlined, SendOutlined, SmileOutlined } from '@ant-design/icons'
import { Button } from '@crosscode/gravity-components'
import { useChat } from './ChatProvider'
import type { ChatMessage } from './types'
import { QUICK_EMOJIS, truncateText } from './utils'
import styles from './Chat.module.css'

interface Props {
  replyTo: ChatMessage | null
  editing: ChatMessage | null
  onCancelReply: () => void
  onCancelEdit: () => void
}

export function ChatComposer({ replyTo, editing, onCancelReply, onCancelEdit }: Props) {
  const { sendMessage, editMessage, sending, startTyping, stopTyping } = useChat()
  const [text, setText] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [previews, setPreviews] = useState<string[]>([])
  const [emojiOpen, setEmojiOpen] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const areaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (editing) {
      setText(editing.text || '')
      setFiles([])
      setPreviews([])
      requestAnimationFrame(() => areaRef.current?.focus())
    }
  }, [editing])

  useEffect(() => {
    if (replyTo && !editing) {
      requestAnimationFrame(() => areaRef.current?.focus())
    }
  }, [replyTo, editing])

  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f))
    setPreviews(urls)
    return () => urls.forEach((u) => URL.revokeObjectURL(u))
  }, [files])

  useEffect(() => {
    const el = areaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`
  }, [text])

  const clearFiles = () => {
    setFiles([])
    if (fileRef.current) fileRef.current.value = ''
  }

  const submit = async () => {
    const trimmed = text.trim()
    if (editing) {
      if (!trimmed) return
      editMessage(editing.id, trimmed)
      setText('')
      onCancelEdit()
      stopTyping()
      return
    }
    if (!trimmed && !files.length) return
    await sendMessage(trimmed, files, replyTo?.id)
    setText('')
    clearFiles()
    onCancelReply()
    stopTyping()
    setEmojiOpen(false)
  }

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void submit()
    }
  }

  const canSend = Boolean(text.trim() || files.length)

  return (
    <div className={styles.composer}>
      {editing ? (
        <div className={styles.contextBar}>
          <div>
            <div className={styles.contextLabel}>Редактирование</div>
            <div className={styles.contextText}>{truncateText(editing.text || '', 80)}</div>
          </div>
          <Button type="text" size="small" icon={<CloseOutlined />} onClick={onCancelEdit} aria-label="Отменить" />
        </div>
      ) : null}

      {replyTo && !editing ? (
        <div className={styles.contextBar}>
          <div>
            <div className={styles.contextLabel}>Ответ · {replyTo.name}</div>
            <div className={styles.contextText}>{truncateText(replyTo.text || 'Вложение', 80)}</div>
          </div>
          <Button type="text" size="small" icon={<CloseOutlined />} onClick={onCancelReply} aria-label="Отменить" />
        </div>
      ) : null}

      {previews.length ? (
        <div className={styles.previewBar}>
          {previews.map((src, i) => (
            <div key={src} className={styles.previewChip}>
              <img src={src} alt="" />
              <button
                type="button"
                className={styles.previewRemove}
                onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                aria-label="Убрать"
              >
                ×
              </button>
            </div>
          ))}
          <Button size="small" type="text" onClick={clearFiles}>
            Очистить
          </Button>
        </div>
      ) : null}

      {emojiOpen ? (
        <div className={styles.emojiBar}>
          {QUICK_EMOJIS.map((e) => (
            <button
              key={e}
              type="button"
              className={styles.emojiBtn}
              onClick={() => {
                setText((t) => t + e)
                startTyping()
                areaRef.current?.focus()
              }}
            >
              {e}
            </button>
          ))}
        </div>
      ) : null}

      <div className={styles.composerRow}>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          hidden
          onChange={(e) => {
            const list = Array.from(e.target.files || []).slice(0, 10)
            setFiles((prev) => [...prev, ...list].slice(0, 10))
          }}
        />
        <div className={styles.capsule}>
          <button
            type="button"
            className={styles.iconBtn}
            aria-label="Вложение"
            disabled={!!editing}
            onClick={() => fileRef.current?.click()}
          >
            <PaperClipOutlined />
          </button>
          <textarea
            ref={areaRef}
            className={styles.nativeArea}
            value={text}
            rows={1}
            onChange={(e) => {
              setText(e.target.value)
              startTyping()
            }}
            onKeyDown={onKey}
            onBlur={stopTyping}
            placeholder={editing ? 'Измените сообщение…' : 'Сообщение'}
            maxLength={4000}
          />
          <button
            type="button"
            className={styles.iconBtn}
            aria-label="Эмодзи"
            onClick={() => setEmojiOpen((v) => !v)}
          >
            <SmileOutlined />
          </button>
        </div>
        <Button
          className={styles.sendBtn}
          type="primary"
          icon={<SendOutlined />}
          loading={sending}
          disabled={!canSend}
          aria-label={editing ? 'Сохранить' : 'Отправить'}
          onClick={() => void submit()}
        />
      </div>
    </div>
  )
}
